/**************************************
 * 1. GLOBAL CART LOCK STATE
 **************************************/
let cartIsLocked = false;

function lockCart() {
  cartIsLocked = true;
  document.body.classList.add('loading');
}

function unlockCart() {
  cartIsLocked = false;
  document.body.classList.remove('loading');
}

function isCartLocked() {
  return cartIsLocked;
}


/**************************************
 * 2. SHOPIFY CART API HELPERS
 **************************************/
async function fetchCart() {
  const response = await fetch('/cart.js');
  if (!response.ok) throw new Error('Failed to fetch cart');
  return response.json();
}

async function updateCartItemQuantity(lineItemKey, quantity) {
  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: lineItemKey, quantity })
  });
  if (!response.ok) throw new Error('Failed to update cart item');
  return response.json();
}

async function removeCartItem(lineItemKey) {
  return updateCartItemQuantity(lineItemKey, 0);
}

async function addItemToCart(variantId, quantity = 1, properties = {}) {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity, properties })
  });
  if (!response.ok) throw new Error('Failed to add cart item');
  return response.json();
}

/**
 * Rewrites a line's properties in place.
 * /cart/change.js REPLACES the whole properties object, so callers must pass a
 * merged set, never a patch. Note the line's `key` changes as a result — the
 * caller has to re-find the line in the returned cart.
 */
async function setCartItemProperties(lineItemKey, quantity, properties) {
  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: lineItemKey, quantity, properties })
  });
  if (!response.ok) throw new Error('Failed to update cart item properties');
  return response.json();
}

/** Same MM-DD-<base36> format the PDP add-to-cart uses. */
function generateGroupKey() {
  const now = new Date();
  const datePrefix =
    String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
  return datePrefix + '-' + Math.random().toString(36).slice(2, 10);
}


/**************************************
 * 3. GHOST ITEM LOGIC
 **************************************/
/**
 * Ghost products.
 *
 * qtySync
 *   true  - the ghost line tracks the main line's quantity. The IGI
 *           certificate is charged per unit ($95 x qty).
 *   false - the ghost line stays pinned at quantity 1 however many units of
 *           the main product are ordered. Priority Delivery is a flat fee per
 *           product, so qty 3 still costs $25 exactly once.
 */
// Populated by snippets/ghost-products-config.liquid, rendered in <head> from
// layout/theme.liquid. Variant IDs and prices come from the real Shopify
// products, so nothing here is hardcoded and nothing can go stale when a
// merchant edits theme settings.
//
// To add a ghost product: add it to that snippet — see
// GHOST-PRODUCTS-AND-DRAWERS.md. Nothing in this file needs to change.
const GHOST_PRODUCTS = window.GHOST_PRODUCTS_CONFIG || [];

function getItemGroupKey(item) {
  return item.properties?._uniquekey_;
}

// NAME THIS CAREFULLY. This file has no IIFE wrapper, so every function
// declared here becomes a global. It was previously called `getGhostConfig`,
// which silently overwrote `window.getGhostConfig(name)` — the name-based
// lookup that snippets/ghost-products-config.liquid publishes in <head>.
//
// The collision only bit on the cart page, where both scripts load:
// custom-cart-detail.js (main-cart-items.liquid:7) runs before
// priority-delivery.js (main-cart-items.liquid:221), so PD read the clobbered
// item-based version, got null back from getGhostConfig('priority_delivery'),
// and fell back to variantId 0 / fee 0 — the cart drawer showed
// "Add guaranteed delivery — $0" and adding the date silently did nothing.
// The PDP was unaffected because custom-cart-detail.js does not load there.
//
// Keep this name distinct from the registry's public getGhostConfig(name).
function getGhostConfigForItem(item) {
  return GHOST_PRODUCTS.find(g => g.variantId === item.variant_id) || null;
}

function isGhostProduct(item) {
  return GHOST_PRODUCTS.some(g => g.variantId === item.variant_id);
}

function isGhostItemLinked(item, mainItem) {
  return (
    isGhostProduct(item) &&
    item.properties?._associatevariantid == mainItem.variant_id &&
    getItemGroupKey(item) === getItemGroupKey(mainItem)
  );
}

function isMatchingGhost(item, variantId, groupKey, ghostConfigVariantId) {
  return (
    item.variant_id === ghostConfigVariantId &&
    item.properties?._associatevariantid == variantId &&
    item.properties?._uniquekey_ === groupKey
  );
}

// Resolve the parent line WITHOUT trusting the line key baked into the DOM.
//
// Writing properties to a line changes its `key` (Shopify derives the key from
// variant + properties). The cart markup renders `data-line-item-key` once, at
// page load, so the moment anything rewrites a line's properties every rendered
// key for that line is stale — and `cart.items.find(i => i.key === domKey)`
// silently returns undefined.
//
// That is what made the cart feel random: the handler threw 'Main product not
// found', the catch swallowed it, no reload followed, and the checkbox sat there
// looking toggled while nothing had happened.
//
// `_uniquekey_` is the identifier that survives a property rewrite, so prefer
// it and keep the key only as a fast path. The variant-only fallback is
// deliberately refused when it is ambiguous — two lines of the same variant
// (same product added twice with different dates) must not be confused.
function findMainItem(cart, lineItemKey, variantId, groupKey) {
  let item = cart.items.find(i => i.key === lineItemKey);
  if (item) return item;

  if (groupKey) {
    item = cart.items.find(
      i => !isGhostProduct(i) && getItemGroupKey(i) === groupKey && i.variant_id == variantId
    );
    if (item) return item;
  }

  const candidates = cart.items.filter(
    i => !isGhostProduct(i) && i.variant_id == variantId
  );
  return candidates.length === 1 ? candidates[0] : null;
}


/**************************************
 * 4. CORE FUNCTIONAL FLOWS
 **************************************/
async function removeMainAndLinkedItems(lineItemKey) {
  if (isCartLocked()) return;
  lockCart();

  try {
    const cart = await fetchCart();
    // Resolve without trusting the DOM's key — see findMainItem().
    const mainItem = findMainItem(cart, lineItemKey, null, null);
    if (!mainItem) return location.reload();

    const itemsToRemove = cart.items.filter(item =>
      item.key === mainItem.key || isGhostItemLinked(item, mainItem)
    );

    for (const item of itemsToRemove) {
      await removeCartItem(item.key);
    }

    // Stay locked through the reload, so the overlay keeps blocking input on a
    // page whose rendered line keys these removals have just invalidated.
    location.reload();
  } catch (error) {
    console.error('Failed to remove item group:', error);
    location.reload();
  }
}

async function updateGroupQuantities(lineItemKey, newQuantity) {
  if (isCartLocked()) return;
  lockCart();
  try {
    const cart = await fetchCart();
    // Never trust the DOM's line key on its own — see findMainItem().
    const mainItem = findMainItem(cart, lineItemKey, null, null);
    if (!mainItem) throw new Error('Main item not found');

    const groupKey = getItemGroupKey(mainItem);

      if (!groupKey) {
        await updateCartItemQuantity(mainItem.key, newQuantity);
      } else {
      const relatedItems = cart.items.filter(item => getItemGroupKey(item) === groupKey);
        for (const item of relatedItems) {
          // Flat-fee ghosts (qtySync:false, e.g. Priority Delivery) are charged
          // once per product, so they must NOT follow the main line's quantity.
          const ghostConfig = getGhostConfigForItem(item);
          if (ghostConfig && ghostConfig.qtySync === false) continue;

          await updateCartItemQuantity(item.key, newQuantity);
        }
      }
    // Stay locked. unlockCart() here would drop the overlay and re-enable every
    // cart control while this reload is still pending, handing the user a live
    // page whose rendered line keys are already stale.
    location.reload();
  } catch (error) {
    // Reload rather than swallow: the rendered state no longer matches the cart,
    // and leaving it as-is is what made the cart look like it randomly ignored
    // clicks. A reload re-renders from server truth and releases the lock.
    console.error('Failed to update group quantity:', error);
    location.reload();
  }
}

async function handleGhostCheckboxToggle(checkboxElement) {
  if (isCartLocked()) return;
  lockCart();

  const isChecked = checkboxElement.checked;
  const cartItemElement = $(checkboxElement).closest('.js-cart-item');
  const lineItemKey = cartItemElement.data('line-key') || cartItemElement.data('line-item-key');
  const variantId = cartItemElement.data('variantid') || cartItemElement.data('variant-id');
  // Reassigned below when the main line has no group key yet.
  let groupKey = cartItemElement.data('uniquekey') || cartItemElement.data('unique-key');
  // Prefer the control's own ghost type so one cart row can host more than one
  // add-on; fall back to the row for the original IGI certificate markup.
  const ghostType = $(checkboxElement).data('ghost-type') || cartItemElement.data('ghost-type');

  const ghostConfig = GHOST_PRODUCTS.find(g => g.name === ghostType);
  if (!ghostConfig) {
    console.warn('Unknown ghost type:', ghostType);
    unlockCart();
    return;
  }

  try {
    let cart = await fetchCart();
    // The DOM's line key goes stale the moment anything rewrites this line's
    // properties (the PD drawer, or the group-key stamp below). Resolve through
    // the identifiers that survive instead — see findMainItem().
    let mainItem = findMainItem(cart, lineItemKey, variantId, groupKey);
    if (!mainItem) throw new Error('Main product not found');
    // The line may have gained a group key since the page rendered.
    if (!groupKey) groupKey = getItemGroupKey(mainItem) || groupKey;

    if (isChecked) {
      // Lines added outside the PDP flow (Dawn quick-add, buy-buttons) carry no
      // _uniquekey_. Attaching a ghost to one used to produce an unlinkable
      // pair: updateGroupQuantities would then take its !groupKey branch and
      // update ONLY the main line, so the cart displayed "$95 x qty" while
      // Shopify still charged for a single certificate.
      //
      // Stamp a group key onto the main line first so every ghost is properly
      // linked and quantity sync always works.
      if (!groupKey) {
        groupKey = generateGroupKey();

        const mergedProps = Object.assign({}, mainItem.properties || {}, {
          _uniquekey_: groupKey,
          _associatevariantid: String(mainItem.variant_id)
        });

        // Rewriting properties changes the line key, so re-find the line.
        cart = await setCartItemProperties(mainItem.key, mainItem.quantity, mergedProps);
        mainItem = cart.items.find(
          item => item.properties && item.properties._uniquekey_ === groupKey
        );
        if (!mainItem) throw new Error('Main product not found after linking');
      }

      const ghostExists = cart.items.some(item =>
        isMatchingGhost(item, variantId, groupKey, ghostConfig.variantId)
      );

      if (!ghostExists) {
        // Honour qtySync on the way IN, exactly as updateGroupQuantities does on
        // every subsequent quantity change. Without this a flat-fee ghost
        // (qtySync:false) attached to a qty-3 line would be added at quantity 3
        // and charged three times — and then silently corrected the next time
        // the shopper touched the stepper, which is the worst kind of bug.
        //
        // Not reachable while the certificate (qtySync:true) is the only ghost
        // with a checkbox, but it is the first thing a new flat-fee ghost would
        // hit. See GHOST-PRODUCTS-AND-DRAWERS.md "Adding a new ghost product".
        const ghostQty = ghostConfig.qtySync === false ? 1 : mainItem.quantity;

        await addItemToCart(ghostConfig.variantId, ghostQty, {
          _associatevariantid: String(variantId),
          _uniquekey_: groupKey
        });
      }
    } else {
      // Tolerant match on the way out: legacy carts may hold ghosts that were
      // added before the fix above and have no group key at all. Falling back to
      // the variant pairing lets those be removed instead of getting stuck.
      const ghostItems = cart.items.filter(function (item) {
        if (isMatchingGhost(item, variantId, groupKey, ghostConfig.variantId)) return true;
        if (groupKey) return false;
        return (
          item.variant_id === ghostConfig.variantId &&
          item.properties &&
          item.properties._associatevariantid == variantId
        );
      });

      for (const ghostItem of ghostItems) {
        await removeCartItem(ghostItem.key);
      }
    }

    // Reload immediately and stay locked until it lands.
    //
    // This used to be `setTimeout(reload, 300)` with `unlockCart()` in a
    // `finally`. The finally ran first, so for at least 300ms the overlay was
    // down, isCartLocked() was false, and every cart control was live again —
    // on a page whose rendered line keys the operation had just invalidated. A
    // click in that window either resolved to nothing and was swallowed by the
    // catch, or had its request aborted by the reload landing mid-flight.
    // That window was the whole "sometimes it adds, sometimes it doesn't".
    location.reload();
  } catch (error) {
    // Recover instead of leaving a checkbox that lies about what happened.
    console.error('Ghost toggle failed:', error);
    location.reload();
  }
}


/**************************************
 * 5. EVENT LISTENERS
 **************************************/
$(document).on('click keypress', '.custom-cart-remove-button', function (event) {
  if (event.type === 'click' || ['Enter', ' '].includes(event.key)) {
    const lineItemKey = $(this).data('line-item-key');
    removeMainAndLinkedItems(lineItemKey);
  }
});

let quantityChangeTimer = null;
$(document).on('click keypress', '.custom-cart-increment-quantity, .custom-cart-decrement-quantity', function (event) {
  if (event.type !== 'click' && !['Enter', ' '].includes(event.key)) return;

  const button = $(this);
  const container = button.closest('.custom-product-quantity');
  const input = container.find('input[name="quantity"]');
  const lineItemKey = container.data('line-item-key');
  const direction = parseInt(button.data('direction'));
  const currentQuantity = parseInt(input.val());
  const newQuantity = Math.max(1, currentQuantity + direction);

  input.val(newQuantity);
  container.find('.custom-cart-decrement-quantity').prop('disabled', newQuantity <= 1);

  clearTimeout(quantityChangeTimer);
  quantityChangeTimer = setTimeout(() => {
    updateGroupQuantities(lineItemKey, newQuantity);
  }, 250);
});

$(document).on('change', '.remove-matching-products', function () {
  handleGhostCheckboxToggle(this);
});


